import React, { useState } from "react";
import styles from "../../_css/ReviewMangement.module.css";
import { X, ChevronLeft, ChevronRight, AudioLines } from "lucide-react";
import StarReview from "../common/StarReview";
import { Form } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import {
  CustomButton,
  CustomButtonDanger,
  CustomButtonOutlineLight,
} from "../UI/CustomButtons";
import ApprovePopup from "./ApprovePopup";
import RejectReviewPopup from "./RejectReviewPopup";
import { formatDate } from "../helper/formatters";

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
    position: "fixed",
  }),
  center: {
    x: 0,
    opacity: 1,
    position: "fixed",
  },
  exit: (direction) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0,
    position: "fixed",
  }),
};

const ReviewCarouselPopup = ({
  open,
  onClose,
  reviews,
  initialIndex,
  handleReviewUpdate,
  handleReviewStatusUpdate,
  approvePopupOpen,
  rejectReviewPopupOpen,
}) => {
  const [direction, setDirection] = useState(0); // -1 for prev, +1 for next
  const [currentIdx, setCurrentIdx] = useState(initialIndex || 0);
  const [adminComment, setAdminComment] = useState("");

  React.useEffect(() => {
    setCurrentIdx(initialIndex || 0);
  }, [initialIndex, open]);

  if (!open) return null;
  const review = reviews[currentIdx];
  const isPrevDisabled = currentIdx === 0;
  const isNextDisabled = currentIdx === reviews.length - 1;

  const statusText = review.status === 0 ? "Pending" : review.status === 1 ? "Approved" : "Rejected";

  return (
    <div className={styles.overlay}>
      <ApprovePopup
        open={approvePopupOpen}
        onClose={() => handleReviewUpdate(1)}
      />
      <RejectReviewPopup
        open={rejectReviewPopupOpen}
        onClose={() => handleReviewUpdate(2)}
      />
      <AnimatePresence initial={false} custom={direction}>
        <button
          style={{ marginRight: "40rem" }}
          className={isPrevDisabled ? styles.navBtnDisabled : styles.navBtn}
          onClick={() => {
            setDirection(-1);
            setCurrentIdx((idx) => Math.max(0, idx - 1));
          }}
          disabled={isPrevDisabled}
        >
          <ChevronLeft size={20} />
        </button>
        <motion.div
          key={currentIdx} // Important: triggers re-animation
          // className={styles.overlay}
          custom={direction}
          initial="enter"
          animate="center"
          exit="exit"
          variants={variants}
          transition={{ duration: 0.4 }}
        >
          <div className={styles.carouselPopup}>
            <div
              className={`${styles.header} d-flex justify-content-between align-items-center mb-2`}
            >
              <h4 className="m-0">Review Details</h4>
              <button className={styles.closeBtn} onClick={onClose}>
                <X size={22} />
              </button>
            </div>
            <div className={styles.carouselHeader}>
              <div className={styles.secondaryHeader}>
                <div className={styles.userInfoSection}>
                  {/* Avatar and user info */}
                  <img
                    src={review.avatar}
                    alt={review.fullName}
                    className={styles.avatar}
                  />
                  <div>
                    <div className={styles.userName}>{review.fullName}</div>
                    <div className={styles.userEmail}>{review.email}</div>
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <span className={styles.voiceBadge}>
                    {" "}
                    <AudioLines size={13} /> Voice
                  </span>
                  <span className={styles.lightBadge}>{review.language}</span>
                  <span className={styles.lightBadge}>Total {review.review_count} Reviews</span>
                </div>
              </div>
              <hr />
              <div className={styles.reviewMetaSection}>
                <div className={styles.reviewDate}>
                  <p>Review Submitted</p>
                  <p>Submitted on {formatDate(review.date)}</p>
                </div>
                <div className={styles.reviewCategory}>
                  Category: {review.category}
                </div>
                <div className={styles.reviewSessionTitle}>
                  Session Title: {review.sessionTitle}
                </div>
                <div className={styles.reviewRating}>
                  Rating{" "}
                  <StarReview
                    rating={review.rating}
                    size={13}
                    padding="2.5px"
                  />
                </div>
              </div>
            </div>
            <div className={styles.reviewContent}>{review.content}</div>
            {review.status === 0 ? (
              <div className="mt-3">
                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Control
                    type="text"
                    placeholder="Write a note for your own reference(optional)"
                    style={{
                      backgroundColor: "#f5f5fa",
                      fontSize: "13px",
                      fontStyle: "italic",
                    }}
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                  />
                </Form.Group>
              </div>
            ) : (
              <div className={`${styles.reviewDate} mt-3 mb-3`}>
                <p>
                  <strong>
                    {statusText} by {review?.reviewd_by} for: {review?.adminComment}
                  </strong>
                </p>
                <p>
                  {statusText} on {formatDate(review.date)}
                </p>
              </div>
            )}
            <div className={styles.carouselFooter}>
              {review.status === 0 ? (
                <CustomButtonOutlineLight
                  text="Reject Review"
                  onClick={() => {
                    handleReviewStatusUpdate([review.id], 2, adminComment);
                  }}
                  width="100%"
                  icon="cancel"
                />
              ) : (
                <CustomButtonOutlineLight
                  text="Close"
                  width="100%"
                  onClick={onClose}
                />
              )}
              {review.status === 0 ? (
                <CustomButton
                  text="Approve Review"
                  onClick={() => {
                    handleReviewStatusUpdate([review.id], 1, adminComment);
                  }}
                  width="100%"
                  icon="tick"
                />
              ) : (
                <span className={styles[statusText]}>
                  Review {statusText}
                </span>
              )}
              {/* <span className={styles.carouselIndex}>{`${currentIdx + 1} / ${
            reviews.length
          }`}</span> */}
            </div>
          </div>
        </motion.div>
        <button
          className={isNextDisabled ? styles.navBtnDisabled : styles.navBtn}
          onClick={() => {
            setDirection(1);
            setCurrentIdx((idx) => Math.min(reviews.length - 1, idx + 1));
          }}
          disabled={isNextDisabled}
        >
          <ChevronRight size={20} />
        </button>
      </AnimatePresence>
    </div>
  );
};

export default ReviewCarouselPopup;
